import { type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { type StudentContext } from './home/model';
import { useCurrentContext } from '../app/ViewContext';
import { EmptyState } from '../components/ui';
import { ALL_FACES, FaceAvatar, useFace } from './home/LineArt';
import { DARK, INK, INK_SOFT, PAPER } from './home/ShapesArt';
import { Head } from './home/ShapesDay';

/*
 * «Налаштування» (his words 28.10: «как кастомизировать аватар? лучше пункт настроек, и там всё менять»).
 * For now the student's profile and the avatar: a face drawn in our one line (no photos of children),
 * kept in this browser; every avatar of the student — the home, the menu — follows it at once.
 */

export function SettingsPage() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  if (ctx.kind !== 'student') return <EmptyState>{t('settings.empty')}</EmptyState>;
  return <StudentSettings ctx={ctx} />;
}

function StudentSettings({ ctx }: { ctx: StudentContext }) {
  const { t } = useTranslation();
  const [face, setFace] = useFace(ctx.child.memberId);
  const initials = `${ctx.child.firstName[0] ?? ''}${ctx.child.lastName[0] ?? ''}`.toUpperCase();
  return (
    <div className="flex flex-col gap-4">
      <section className="flex min-w-0 flex-col gap-5 rounded-[28px] bg-white p-5 sm:flex-row sm:items-center md:p-6">
        <FaceAvatar face={face} initials={initials} size={112} />
        <div className="min-w-0">
          <p className="text-[13px] font-medium" style={{ color: INK_SOFT }}>
            {t('settings.title')}
          </p>
          <h1
            className="font-display text-[40px] leading-[46px] font-light tracking-[-0.04em]"
            style={{ color: INK }}
          >
            {ctx.child.firstName} {ctx.child.lastName}
          </h1>
          <p className="mt-1 text-[14px]" style={{ color: INK_SOFT }}>
            {[ctx.child.class?.label, ctx.schoolName].filter(Boolean).join(' · ')}
          </p>
        </div>
      </section>
      <section className="rounded-[28px] bg-white p-5 md:p-6">
        <Head title={t('settings.avatar')} note={t('settings.avatarNote')} />
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-5 xl:grid-cols-9">
          {ALL_FACES.map((f, i) => (
            <button
              key={f}
              type="button"
              aria-pressed={f === face}
              aria-label={f === 'initials' ? t('studentHome.shapes2.initials') : undefined}
              onClick={() => setFace(f)}
              className="motion-pop flex justify-center rounded-[24px] py-3 transition-[background-color,transform] duration-300 ease-spring hover:-translate-y-1"
              style={
                {
                  '--delay': `${i * 0.035}s`,
                  backgroundColor: f === face ? PAPER : undefined,
                  boxShadow: f === face ? `inset 0 0 0 2px ${DARK}` : undefined,
                } as CSSProperties
              }
            >
              <FaceAvatar face={f} initials={initials} size={80} />
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
